/* Explicit private-project selection. Loading this module performs no request. */
(function (global, factory) {
  'use strict';
  const api = factory(global);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (global) global.TodayMerchPersonalizationProjectScope = api;
})(typeof window !== 'undefined' ? window : null, function (global) {
  'use strict';
  const JOURNEY = '/apps/todays-studio/project-api';
  const JOURNAL = '/apps/todays-studio/personalization-designs';
  const UUID = /^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/iu;
  const NUMERIC_ID = /^[1-9][0-9]{0,29}$/u;
  const ID = /^[A-Za-z0-9][A-Za-z0-9_.:-]{0,179}$/u;
  const copy = value => structuredClone(value);
  const error = (code, status = 0) => Object.assign(new Error(code), { code, status });
  const pick = (source, fields) => Object.fromEntries(fields.filter(key => source[key] !== undefined).map(key => [key, copy(source[key])]));
  const validId = value => typeof value === 'string' && ID.test(value) && !['__proto__', 'constructor', 'prototype'].includes(value);
  const zoneOf = current => current?.selected?.zoneKey || current?.zoneKey || '';
  const artworkOf = current => current?.selected?.artworkId || current?.artworkId || 'base';
  const ownerOf = (root, current) => `${root?.dataset.productId || ''}|${current?.variantId || ''}|${zoneOf(current)}|${artworkOf(current)}`;
  const ITEM_FIELDS = ['id', 'title', 'variant', 'category', 'image', 'quantity', 'minQuantity', 'increment', 'maxQuantity',
    'unitPriceCents', 'purchaseMode', 'personalized', 'requestHelp', 'designs', 'productUrl', 'leadTime', 'variantId', 'productId',
    'sku', 'cartLineKey', 'artworkFiles', 'personalization', 'pricing'];
  const DESIGN_FIELDS = ['id', 'placement', 'technique', 'colors', 'widthMm', 'heightMm', 'previewUrl', 'previewStatus',
    'fileName', 'fileUrl', 'approved', 'approvalFingerprint', 'artworkBinding', 'xdV2Assets', 'sourceArtworkId', 'sourceZoneKey'];
  function publicProject(project) {
    if (!project || !Array.isArray(project.items)) throw error('invalid-project');
    const output = pick(project, ['version', 'name', 'step', 'items', 'account', 'profile', 'shipping', 'tax', 'consent',
      'finalizationChoice', 'paymentMethod', 'updatedAt']);
    output.items = project.items.map(item => ({ ...pick(item, ITEM_FIELDS),
      designs: (item.designs || []).map(design => pick(design, DESIGN_FIELDS)) }));
    return output;
  }
  function createClient({ fetch: fetcher = global?.fetch?.bind(global), timeoutMs = 20_000 } = {}) {
    if (typeof fetcher !== 'function' || !Number.isSafeInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 60_000) throw new TypeError('A same-origin project transport is required.');
    let csrf = '', customer = '';
    async function request(path, { method = 'GET', body, renew = true, allowMissing = false } = {}) {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const serialized = body === undefined ? undefined : JSON.stringify(body);
        if (serialized && new TextEncoder().encode(serialized).length > 2 * 1024 * 1024) throw error('too-large', 413);
        const response = await fetcher(path, { method, credentials: 'same-origin', redirect: 'error', cache: 'no-store', signal: controller.signal,
          headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json', 'X-Today-Merch-CSRF': csrf }) },
          ...(serialized === undefined ? {} : { body: serialized }) });
        if (response.status === 403 && body !== undefined && renew && customer) {
          const before = customer;
          await session();
          if (customer !== before) throw error('owner-changed', 401);
          return request(path, { method, body, renew: false, allowMissing });
        }
        let value;
        try { value = await response.json(); } catch { throw error('unavailable', response.status || 503); }
        if (allowMissing && response.status === 404 && value?.code === 'PERSONALIZATION_DESIGN_NOT_FOUND') return null;
        if (!response.ok) throw error(response.status === 409 ? 'conflict' : 'unavailable', response.status);
        return value;
      } catch (failure) { if (failure?.code) throw failure; throw error('unavailable', 503); }
      finally { clearTimeout(timer); }
    }
    async function session() {
      const result = await request(`${JOURNEY}/session?purpose=personalization`);
      const next = result?.authenticated === true && /^gid:\/\/shopify\/Customer\/[1-9][0-9]{0,29}$/u.test(result.customer?.id || '') ? result.customer.id : '';
      csrf = typeof result.csrfToken === 'string' ? result.csrfToken : '';
      customer = next;
      if (!customer || !csrf) throw error('authentication-required', 401);
      return result;
    }
    return Object.freeze({ session, list: () => request(`${JOURNEY}/projects`),
      get(projectId) { if (!UUID.test(projectId || '')) throw error('invalid-project'); return request(`${JOURNEY}/projects/${projectId}`); },
      save(record, project, requestId) {
        if (record && (!UUID.test(record.id || '') || !Number.isSafeInteger(record.version) || record.version < 1)
          || !UUID.test(requestId || '')) throw error('invalid-operation');
        return request(`${JOURNEY}/projects${record ? `/${record.id}` : ''}`, { method: record ? 'PUT' : 'POST',
          body: { requestId, ...(record ? { version: record.version } : {}), project } });
      },
      lookupScope(scope) {
        if (!UUID.test(scope?.projectId || '') || !validId(scope.itemId) || !validId(scope.designId)) throw error('invalid-scope');
        const query = ['projectId', 'itemId', 'designId'].map(key => `${key}=${encodeURIComponent(scope[key])}`).join('&');
        return request(`${JOURNAL}/scope?${query}`, { allowMissing: true });
      },
    });
  }
  function create({ enabled = false, root, mount = root, readOwner, model = global?.TMProjectModel,
    client, fetch, checkAvailability, onScopeChange = () => {}, onSaveRequested = () => {}, onChange = () => {},
    readProduct, canChangeScope = () => true, makeRequestId = () => globalThis.crypto.randomUUID() } = {}) {
    if (typeof enabled !== 'boolean' || !root || typeof readOwner !== 'function' || !model?.createProject
      || [onScopeChange, onSaveRequested, onChange].some(callback => typeof callback !== 'function')
      || typeof canChangeScope !== 'function'
      || checkAvailability !== undefined && typeof checkAvailability !== 'function'
      || readProduct !== undefined && typeof readProduct !== 'function') throw new TypeError('Explicit project-scope dependencies are required.');
    client ||= createClient({ fetch });
    const scopes = new Map();
    let actor = '', available = false, destroyed = false, running = false, generation = 0;
    let pending = null, state = 'unavailable', message = 'Enregistrement privé indisponible.', projects = [], selectedRecord = null;
    let dialogOwner = '', dialogKey = '', dialogOriginal = null, projectGeneration = 0, ui = null;
    const currentScope = current => actor ? scopes.get(`${actor}|${ownerOf(root, current)}`) || null : null;
    const sameScope = (left, right) => Boolean(left && right && left.projectId === right.projectId && left.itemId === right.itemId && left.designId === right.designId);
    const scopeMayChange = () => { try { return canChangeScope() === true; } catch { return false; } };
    function assertScopeMayChange() { if (!scopeMayChange()) throw error('scope-locked'); }
    function assertDestination(scope, captured) { if (!sameScope(scope, currentScope(captured.current))) assertScopeMayChange(); }
    const notify = () => { render(); onChange(read()); };
    const assertAlive = () => { if (destroyed) throw error('destroyed'); };
    function assertCurrent(owner, key, original) {
      assertAlive(); const current = readOwner();
      if (ownerOf(root, current) !== owner || current.key !== key || (current.selected?.original || null) !== original) throw error('selection-changed');
      return current;
    }
    async function authenticate(expected = actor) {
      const value = await client.session(); assertAlive();
      const next = value?.customer?.id;
      if (value.authenticated !== true || !/^gid:\/\/shopify\/Customer\/[1-9][0-9]{0,29}$/u.test(next || '')) throw error('authentication-required', 401);
      if (actor && actor !== next || expected && expected !== next) {
        scopes.clear(); pending = null; projects = []; selectedRecord = null; actor = ''; available = false;
        throw error('owner-changed', 401);
      }
      actor = next; return value;
    }
    function handleFailure(failure) {
      if (failure.code === 'scope-locked' || !scopeMayChange() && currentScope(readOwner())) {
        state = 'journal-pending'; message = 'Réessayez d’abord l’enregistrement du design en attente.';
        ui?.dialog.close(); notify(); return;
      }
      available = false;
      if (failure.status === 401 || failure.status === 403) {
        scopes.clear(); pending = null; actor = ''; projects = []; selectedRecord = null;
      }
      if (failure.status >= 400 && failure.status < 500 && failure.status !== 429) pending = null;
      if (failure.status === 409) { pending = null; state = 'conflict'; available = Boolean(actor); message = 'Ce projet a changé ailleurs. Rouvrez le choix de projet pour charger sa dernière version.'; }
      else if (failure.code === 'selection-changed') { state = 'changed'; available = Boolean(actor); message = 'Le produit ou le visuel a changé. Rouvrez le choix de projet.'; }
      else if (pending) { state = 'pending'; message = 'La réponse est incertaine. Réessayez la même sauvegarde avant de choisir un autre projet.'; }
      else if (['zone-unproven', 'invalid-name', 'item-limit', 'design-limit', 'selection-unavailable', 'invalid-scope', 'project-not-selected', 'quantity-mismatch'].includes(failure.code)) {
        available = Boolean(actor); state = 'ready'; message = 'Vérifiez le choix du projet et du design avant de continuer.';
      }
      else { state = 'unavailable'; message = failure.status === 401 ? 'Connectez-vous pour enregistrer dans votre projet privé.' : 'Enregistrement privé indisponible. Votre visuel reste ouvert.'; }
      if (pending || state === 'conflict' || state === 'changed') ui?.dialog.close();
      notify();
    }
    async function refresh() {
      if (!enabled || destroyed || running || typeof checkAvailability !== 'function') return false;
      if (!scopeMayChange() && currentScope(readOwner())) { handleFailure(error('scope-locked')); return false; }
      const ticket = ++generation; available = false; state = 'checking'; notify();
      try {
        const session = await authenticate();
        if (await checkAvailability({ root, session, client }) !== true) throw error('unavailable', 503);
        if (destroyed || ticket !== generation) return false;
        available = true; state = pending ? 'pending' : 'ready';
        message = pending ? 'Réessayez la sauvegarde en attente.' : 'Choisissez un projet privé pour enregistrer ce design.';
        notify(); return true;
      } catch (failure) { if (!destroyed && ticket === generation) handleFailure(failure); return false; }
    }
    function record(value, expected) {
      const result = value?.project;
      if (!UUID.test(result?.id || '') || expected && result.id !== expected || result.status !== 'draft'
        || !Number.isSafeInteger(result.version) || result.version < 1 || !Array.isArray(result.data?.items)) throw error('invalid-project', 409);
      return copy(result);
    }
    function sameItem(item, current) { return item?.productId === root.dataset.productId && item.variantId === current.variantId; }
    function snapshot({ requireArtwork = false } = {}) {
      const current = readOwner(), file = current.selected?.original || null;
      if (!NUMERIC_ID.test(root.dataset.productId || '') || !NUMERIC_ID.test(current.variantId || '') || !validId(zoneOf(current))
        || typeof current.key !== 'string' || !current.key
        || requireArtwork && (!file || typeof file.name !== 'string' || !file.name || !current.selected?.techniqueId)) throw error('selection-unavailable');
      return { current, owner: ownerOf(root, current), key: current.key, original: file };
    }
    async function open() {
      if (destroyed || running || pending) return false;
      if (!scopeMayChange()) { handleFailure(error('scope-locked')); return false; }
      if (!await refresh()) return false;
      try {
        const captured = snapshot(), expectedActor = actor, value = await client.list();
        await authenticate(expectedActor);
        assertCurrent(captured.owner, captured.key, captured.original);
        if (!Array.isArray(value?.projects)) throw error('unavailable');
        projects = value.projects.filter(project => project?.status === 'draft' && UUID.test(project.id || '')).slice(0, 100);
        dialogOwner = captured.owner; dialogKey = captured.key; dialogOriginal = captured.original; selectedRecord = null;
        if (ui) {
          fillProjects(); ui.project.value = captured.original ? '' : '__choose__'; ui.name.value = 'Mon projet merch'; ui.item.replaceChildren(option('', 'Ajouter ce produit et son design'));
          ui.design.replaceChildren(option('', 'Créer un nouveau design')); ui.nameWrap.hidden = false; ui.itemWrap.hidden = true; ui.designWrap.hidden = true;
          ui.nameWrap.hidden = !captured.original;
          ui.dialog.querySelector('h2').textContent = captured.original ? 'Enregistrer dans un projet privé' : 'Retrouver un design privé';
          ui.dialog.setAttribute('aria-label', captured.original ? 'Enregistrer dans un projet privé' : 'Retrouver un design privé');
          ui.dialog.querySelector('form > p').textContent = captured.original
            ? 'Le visuel actuel sera enregistré dans le design choisi. Un design déjà enregistré recevra une nouvelle révision. Le projet reste un brouillon.'
            : 'Choisissez un design enregistré dans votre projet. Ses fichiers seront ouverts après votre action Reprendre.';
          ui.feedback.textContent = ''; render(); ui.dialog.showModal(); ui.project.focus();
        }
        return true;
      } catch (failure) { handleFailure(failure); return false; }
    }
    async function selectProject(projectId) {
      if (running || pending) throw error('busy');
      assertScopeMayChange();
      const captured = snapshot(), ticket = ++projectGeneration, expectedActor = actor;
      if (!projects.some(project => project.id === projectId)) throw error('project-not-selected');
      const value = record(await client.get(projectId), projectId);
      await authenticate(expectedActor);
      assertCurrent(captured.owner, captured.key, captured.original);
      if (ticket !== projectGeneration) throw error('selection-changed');
      selectedRecord = value;
      return copy(value);
    }
    async function verifyExisting(scope, item, design, current) {
      if (!sameItem(item, current) || design?.id !== scope.designId) throw error('invalid-scope');
      const bindings = Array.isArray(design.artworkBinding) ? design.artworkBinding : design.artworkBinding ? [design.artworkBinding] : [];
      if (artworkOf(current) !== 'base' && !(design.sourceArtworkId === artworkOf(current) && design.sourceZoneKey === zoneOf(current)) && !bindings.some(value => value.zoneKey === zoneOf(current) && value.artworkId === artworkOf(current))) throw error('zone-unproven', 422);
      const found = await client.lookupScope(scope);
      if (found) {
        if (found.scope?.projectId !== scope.projectId || found.scope.itemId !== scope.itemId || found.scope.designId !== scope.designId
          || found.selection?.productId !== root.dataset.productId || found.selection.variantId !== current.variantId
          || found.selection.positionId !== zoneOf(current) || !UUID.test(found.id || '')) throw error('zone-mismatch', 409);
        return { ...scope, dossierId: found.id };
      }
      if (!current.selected?.original || !(design.sourceArtworkId === artworkOf(current) && design.sourceZoneKey === zoneOf(current)) && (bindings.length !== 1 || bindings[0].zoneKey !== zoneOf(current) || (bindings[0].artworkId || 'base') !== artworkOf(current))) throw error('zone-unproven', 422);
      return scope;
    }
    function makePlaceholder(captured, itemId, designId) {
      const current = captured.current;
      const facts = readProduct ? readProduct({ root, current }) : { title: root.querySelector('h1')?.textContent?.trim() || 'Produit', productUrl: global?.location?.pathname || '' };
      if (!facts || typeof facts.then === 'function') throw error('selection-unavailable');
      const quantity = Number(current.quantity);
      if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 1_000_000) throw error('selection-unavailable');
      const native = root.querySelector('input[name="quantity"]');
      const numberOr = (value, fallback) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : fallback;
      const design = { id: designId, placement: String(current.zone?.label || current.zone?.name || zoneOf(current)).slice(0, 180),
        technique: String(current.selected.techniqueId).slice(0, 180), colors: [], widthMm: null, heightMm: null,
        previewUrl: '', previewStatus: 'unavailable', fileName: captured.original.name.slice(0, 256), approved: false, approvalFingerprint: '',
        ...(artworkOf(current) !== 'base' ? { sourceArtworkId: artworkOf(current), sourceZoneKey: zoneOf(current) } : {}) };
      const item = model.createProject([{ id: itemId, productId: root.dataset.productId, variantId: current.variantId,
        title: String(facts.title || 'Produit').slice(0, 256), variant: String(facts.variant || '').slice(0, 500),
        sku: String(facts.sku || '').slice(0, 256), image: '', productUrl: String(facts.productUrl || '').slice(0, 4096),
        quantity, minQuantity: numberOr(facts.minQuantity ?? native?.min, 1), increment: numberOr(facts.increment ?? native?.step, 1),
        maxQuantity: numberOr(facts.maxQuantity ?? native?.max, 1_000_000), unitPriceCents: null, purchaseMode: 'quote',
        personalized: true, requestHelp: false, designs: [design] }]).items[0];
      return publicProject(model.createProject([item])).items[0];
    }
    async function complete(scope, captured, expectedActor, onBound = () => {}) {
      assertDestination(scope, captured);
      await authenticate(expectedActor);
      assertDestination(scope, captured);
      // Recognize a committed Journey operation without adopting an obsolete visual.
      onBound();
      assertCurrent(captured.owner, captured.key, captured.original);
      scopes.set(`${actor}|${captured.owner}`, copy(scope));
      state = 'scope-ready'; message = 'Projet sélectionné. Enregistrement du design en cours…'; available = true; notify();
      await onScopeChange(copy(scope));
      assertCurrent(captured.owner, captured.key, captured.original);
      if (captured.original && captured.current.selected?.techniqueId) await onSaveRequested(copy(scope));
      state = 'selected'; message = captured.original ? 'Projet privé sélectionné. Le statut du dossier indique si le design est enregistré.'
        : 'Design privé retrouvé. Utilisez Reprendre pour ouvrir ses fichiers dans le Studio.';
      ui?.dialog.close(); notify(); return copy(scope);
    }
    async function runPending() {
      const operation = pending;
      if (!operation) throw error('no-pending-operation');
      await authenticate(operation.actor);
      if (!operation.attempted) assertScopeMayChange();
      operation.attempted = true;
      const value = record(await client.save(operation.record, copy(operation.project), operation.requestId), operation.record?.id);
      // A late response cannot bind a different product, zone, visual or account.
      await authenticate(operation.actor);
      const item = value.data.items.find(item => item.id === operation.itemId), design = item?.designs?.find(design => design.id === operation.designId);
      if (!sameItem(item, operation.captured.current) || !design) throw error('invalid-save-response');
      const scope = { projectId: value.id, itemId: operation.itemId, designId: operation.designId };
      return complete(scope, operation.captured, operation.actor, () => { if (pending === operation) pending = null; });
    }
    async function saveSelected() {
      if (!enabled || destroyed || running || !available) throw error('unavailable');
      const captured = snapshot({ requireArtwork: true }), scope = currentScope(captured.current);
      if (!scope) throw error('scope-unavailable');
      running = true; state = 'saving'; notify();
      try { return await complete(scope, captured, actor); }
      catch (failure) { handleFailure(failure); throw failure; }
      finally { running = false; if (!destroyed) notify(); }
    }
    async function save({ projectId = null, itemId = null, designId = null, name = 'Mon projet merch' } = {}) {
      if (!enabled || destroyed || running || pending || !available) throw error('unavailable');
      if (!scopeMayChange()) { const failure = error('scope-locked'); handleFailure(failure); throw failure; }
      running = true; state = 'saving'; notify();
      try {
        const captured = snapshot(), expectedActor = actor;
        await authenticate(expectedActor); assertCurrent(captured.owner, captured.key, captured.original);
        let projectRecord = null;
        if (projectId) {
          if (!projects.some(project => project.id === projectId) || selectedRecord?.id !== projectId) throw error('project-not-selected');
          projectRecord = record(await client.get(projectId), projectId);
        } else if (itemId || designId) throw error('invalid-scope');
        assertCurrent(captured.owner, captured.key, captured.original);
        const target = itemId ? projectRecord?.data.items.find(item => item.id === itemId) : null;
        if (itemId && !sameItem(target, captured.current)) throw error('invalid-scope');
        if (designId) {
          if (!target) throw error('invalid-scope');
          const design = target.designs.find(value => value.id === designId);
          const scope = await verifyExisting({ projectId, itemId, designId }, target, design, captured.current);
          return await complete(scope, captured, expectedActor);
        }
        if (target && target.quantity !== Number(captured.current.quantity)) throw error('quantity-mismatch', 422);
        snapshot({ requireArtwork: true });
        const newItemId = itemId || makeRequestId(), newDesignId = makeRequestId(), requestId = makeRequestId();
        if (![newItemId, newDesignId, requestId].every(validId) || !UUID.test(requestId)) throw error('invalid-operation');
        const placeholder = makePlaceholder(captured, newItemId, newDesignId);
        const project = publicProject(projectRecord ? projectRecord.data : model.createProject([]));
        if (!projectRecord) {
          if (typeof name !== 'string' || !name.trim() || name.trim().length > 80) throw error('invalid-name', 422);
          project.name = name.trim();
        }
        if (target) {
          const line = project.items.find(item => item.id === itemId);
          if (line.designs.length >= 32) throw error('design-limit', 422);
          line.designs.push(placeholder.designs[0]);
        } else {
          if (project.items.length >= 50) throw error('item-limit', 422);
          project.items.push(placeholder);
        }
        assertCurrent(captured.owner, captured.key, captured.original);
        assertScopeMayChange();
        pending = { actor: expectedActor, captured, record: projectRecord ? { id: projectRecord.id, version: projectRecord.version } : null,
          project: copy(project), requestId, itemId: newItemId, designId: newDesignId, attempted: false };
        return await runPending();
      } catch (failure) { handleFailure(failure); throw failure; }
      finally { running = false; if (!destroyed) notify(); }
    }
    async function retry() {
      if (!enabled || destroyed || running || !pending) throw error('no-pending-operation');
      running = true; state = 'saving'; notify();
      try { return await runPending(); }
      catch (failure) { handleFailure(failure); throw failure; }
      finally { running = false; if (!destroyed) notify(); }
    }
    function read() { return Object.freeze({ enabled, available, state, message, running, pending: Boolean(pending), scope: currentScope(readOwner()) ? copy(currentScope(readOwner())) : null }); }
    function option(value, text) { const element = root.ownerDocument.createElement('option'); element.value = value; element.textContent = text; return element; }
    function fillProjects() {
      const placeholder = option('__choose__', 'Choisir un projet existant'); placeholder.disabled = true;
      const fresh = option('', 'Créer un nouveau projet'); fresh.disabled = !dialogOriginal;
      ui.project.replaceChildren(placeholder, fresh, ...projects.map(project => option(project.id, project.name || 'Projet sans nom')));
    }
    function render() {
      if (!ui) return;
      let selectionAvailable = false, artworkAvailable = false;
      try { snapshot(); selectionAvailable = true; } catch { /* No native context is selected. */ }
      try { snapshot({ requireArtwork: true }); artworkAvailable = true; } catch { /* Existing designs can still be reopened. */ }
      ui.launch.hidden = !available || Boolean(pending) || !selectionAvailable; ui.launch.disabled = running || !scopeMayChange();
      ui.launch.textContent = currentScope(readOwner()) ? 'Choisir un autre projet ou design' : artworkAvailable ? 'Enregistrer dans un projet' : 'Retrouver un projet';
      ui.save.hidden = !available || !artworkAvailable || !currentScope(readOwner()); ui.save.disabled = running;
      ui.save.textContent = scopeMayChange() ? 'Enregistrer ce design' : 'Réessayer l’enregistrement du design';
      ui.retry.hidden = !pending; ui.retry.disabled = running;
      ui.status.textContent = message;
      const validChoice = ui.project.value !== '__choose__' && (artworkAvailable || Boolean(ui.project.value && ui.item.value && ui.design.value));
      ui.confirm.disabled = running || !available || Boolean(pending) || !validChoice || !scopeMayChange();
      ui.confirm.textContent = artworkAvailable ? 'Enregistrer' : 'Retrouver ce design';
      ui.project.disabled = running; ui.item.disabled = running; ui.design.disabled = running;
    }
    function mountUI() {
      if (!enabled || !mount?.append || !root.ownerDocument?.createElement) return;
      const document = root.ownerDocument, container = document.createElement('div'); container.className = 'personalization-project-scope';
      container.innerHTML = '<button type="button" data-project-scope-launch hidden>Enregistrer dans un projet</button><button type="button" data-project-scope-save hidden>Enregistrer ce design</button><button type="button" data-project-scope-retry hidden>Réessayer la même sauvegarde</button><p data-project-scope-status role="status" aria-live="polite"></p><dialog class="personalization-project-scope__dialog" aria-label="Enregistrer dans un projet privé"><form><h2>Enregistrer dans un projet privé</h2><p>Choisissez la destination de ce produit et du design de la zone actuelle. Le projet reste un brouillon.</p><label>Projet<select data-project-scope-project></select></label><label data-project-scope-name-wrap>Nom du nouveau projet<input data-project-scope-name maxlength="80" required></label><label data-project-scope-item-wrap hidden>Ligne du produit<select data-project-scope-item></select></label><label data-project-scope-design-wrap hidden>Design de cette ligne<select data-project-scope-design></select></label><p data-project-scope-feedback role="alert"></p><div class="personalization-project-scope__actions"><button type="button" data-project-scope-cancel>Annuler</button><button type="button" data-project-scope-confirm>Enregistrer</button></div></form></dialog>';
      mount.append(container);
      const find = selector => container.querySelector(selector);
      ui = { container, launch: find('[data-project-scope-launch]'), save: find('[data-project-scope-save]'), retry: find('[data-project-scope-retry]'), status: find('[data-project-scope-status]'),
        dialog: find('dialog'), project: find('[data-project-scope-project]'), name: find('[data-project-scope-name]'), nameWrap: find('[data-project-scope-name-wrap]'),
        item: find('[data-project-scope-item]'), itemWrap: find('[data-project-scope-item-wrap]'), design: find('[data-project-scope-design]'), designWrap: find('[data-project-scope-design-wrap]'),
        feedback: find('[data-project-scope-feedback]'), confirm: find('[data-project-scope-confirm]') };
      ui.launch.addEventListener('click', () => { void open(); }); ui.retry.addEventListener('click', () => { void retry().catch(() => {}); });
      ui.save.addEventListener('click', () => { void saveSelected().catch(() => {}); });
      find('form').addEventListener('submit', event => { event.preventDefault(); if (!ui.confirm.disabled) ui.confirm.click(); });
      find('[data-project-scope-cancel]').addEventListener('click', () => ui.dialog.close());
      ui.project.addEventListener('change', async () => {
        const selected = ui.project.value, ticket = ++generation; selectedRecord = null; ui.confirm.disabled = true;
        ui.nameWrap.hidden = Boolean(selected); ui.itemWrap.hidden = !selected; ui.designWrap.hidden = true; ui.feedback.textContent = '';
        ui.item.replaceChildren(option('', 'Ajouter ce produit et son design')); ui.design.replaceChildren(option('', 'Créer un nouveau design'));
        ui.item.options[0].disabled = !dialogOriginal;
        if (!selected) { render(); return; }
        try {
          const project = await selectProject(selected);
          if (destroyed || ticket !== generation || ui.project.value !== selected) return;
          ui.item.append(...project.data.items.filter(item => sameItem(item, readOwner())).map(item => option(item.id, `${item.title} — ${item.quantity} unités — ${item.designs.length} design(s)`)));
          render();
        } catch (failure) { handleFailure(failure); ui.feedback.textContent = 'Ce projet ne peut pas être ouvert. Choisissez un autre brouillon.'; }
      });
      ui.item.addEventListener('change', () => {
        ui.design.replaceChildren(option('', 'Créer un nouveau design pour cette zone'));
        const item = selectedRecord?.data.items.find(item => item.id === ui.item.value); ui.designWrap.hidden = !item;
        ui.design.options[0].disabled = !dialogOriginal;
        if (item) ui.design.append(...item.designs.map(design => option(design.id, `${design.placement || 'Design'} — ${design.fileName || design.technique || design.id}`)));
        render();
      });
      ui.design.addEventListener('change', render);
      ui.confirm.addEventListener('click', async () => {
        try {
          assertCurrent(dialogOwner, dialogKey, dialogOriginal);
          await save({ projectId: ui.project.value || null, itemId: ui.project.value ? ui.item.value || null : null,
            designId: ui.project.value && ui.item.value ? ui.design.value || null : null, name: ui.name.value });
        } catch (failure) { ui.feedback.textContent = failure.code === 'zone-unproven' || failure.code === 'zone-mismatch'
          ? 'Ce design ne correspond pas à la zone actuelle ou sa zone ne peut pas être vérifiée. Choisissez un nouveau design.'
          : failure.code === 'quantity-mismatch' ? 'La quantité de cette ligne diffère de votre sélection actuelle. Ajoutez ce produit sur une nouvelle ligne.' : message; }
      });
      render();
    }
    mountUI();
    const api = Object.freeze({ refresh, open, selectProject, save, saveSelected, retry, read,
      resolveScope({ root: requestedRoot = root, current = readOwner() } = {}) {
        if (!enabled || destroyed || requestedRoot !== root || !available || !actor) return null;
        const scope = currentScope(current); return scope ? copy(scope) : null;
      },
      onOwnerChange() {
        generation++;
        if (ui?.dialog.open && ownerOf(root, readOwner()) !== dialogOwner) ui.dialog.close();
        notify();
      },
      pendingOperation() { return pending ? { requestId: pending.requestId, projectId: pending.record?.id || null, itemId: pending.itemId, designId: pending.designId } : null; },
      destroy() { destroyed = true; generation++; available = false; scopes.clear(); pending = null; actor = ''; ui?.container.remove(); ui = null; },
    });
    return api;
  }
  return Object.freeze({ create, createClient, publicProject });
});
